import { getConfig } from '../config';

const CLIENT_IP_HEADER = 'ip_address';
const CLIENT_IP_STORAGE_KEY = 'schedulerdesk-client-ip';
const DEFAULT_CLIENT_IP_URL = 'https://api.ipify.org?format=json';
const CLIENT_IP_FETCH_TIMEOUT_MS = 1500;

let cachedClientIp = '';
let clientIpPromise = null;

const normalizeValue = (value) => String(value || '').trim();

const readStoredClientIp = () => {
	if (typeof window === 'undefined') return '';

	try {
		return normalizeValue(window.sessionStorage.getItem(CLIENT_IP_STORAGE_KEY));
	} catch (_) {
		return '';
	}
};

const storeClientIp = (clientIp) => {
	if (!clientIp || typeof window === 'undefined') return;

	try {
		window.sessionStorage.setItem(CLIENT_IP_STORAGE_KEY, clientIp);
	} catch (_) {}
};

const extractClientIp = async (response) => {
	const contentType = response.headers.get('content-type') || '';

	if (contentType.includes('application/json')) {
		const payload = await response.json();
		return normalizeValue(payload?.ip || payload?.query || payload?.address);
	}

	return normalizeValue(await response.text());
};

export const getClientIp = async () => {
	if (cachedClientIp) return cachedClientIp;

	const storedClientIp = readStoredClientIp();
	if (storedClientIp) {
		cachedClientIp = storedClientIp;
		return storedClientIp;
	}

	if (!clientIpPromise) {
		clientIpPromise = (async () => {
			const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
			const timeoutId =
				controller && typeof window !== 'undefined'
					? window.setTimeout(() => controller.abort(), CLIENT_IP_FETCH_TIMEOUT_MS)
					: null;

			try {
				const clientIpUrl =
					getConfig().REACT_APP_CLIENT_IP_URL || DEFAULT_CLIENT_IP_URL;
				const response = await fetch(clientIpUrl, {
					method: 'GET',
					headers: {
						Accept: 'application/json, text/plain, */*',
					},
					signal: controller?.signal,
				});

				if (!response.ok) return '';

				const clientIp = await extractClientIp(response);

				if (clientIp) {
					cachedClientIp = clientIp;
					storeClientIp(clientIp);
				}

				return clientIp;
			} catch (_) {
				return '';
			} finally {
				if (timeoutId) {
					window.clearTimeout(timeoutId);
				}
				clientIpPromise = null;
			}
		})();
	}

	return clientIpPromise;
};

export const appendClientIpHeader = async (headers = {}) => {
	const clientIp = await getClientIp();

	if (!clientIp) return headers;

	return {
		...headers,
		[CLIENT_IP_HEADER]: clientIp,
	};
};

export const buildJsonHeaders = async ({ sessionKey = '', headers = {} } = {}) => {
	const requestHeaders = {
		'Content-Type': 'application/json',
		Accept: 'application/json',
		...headers,
	};

	if (sessionKey) {
		requestHeaders['sdk-authorization'] = `${sessionKey}`;
	}

	return appendClientIpHeader(requestHeaders);
};

export { CLIENT_IP_HEADER };
